import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Container, Loading } from "@/components/atoms";
import { GeneralFooter, GeneralHeader } from "@/components/organisms";
import { ClientRegistrationStep } from "@/components/organisms/Onboarding";
import { AccountType } from "@/constants/accountTypes";
import { useLazyGetAccountsQuery } from "@/redux/apis/account/accountApi";
import { useLazyVerifyProviderQuery } from "@/redux/apis/auth/authApi";
import { useLazyGetProfileQuery } from "@/redux/apis/profile/profileApi";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { setActiveAccount } from "@/redux/slices/account/accountSlice";
import { siteInfoSelector } from "@/redux/slices/config/selectors";
import { setActiveOperator } from "@/redux/slices/operator/operatorSlice";

// Type definitions for Kinde claims
type UserProperty = { v: string };
type ClaimsWithUserProperties = {
  user_properties?: {
    kp_usr_utm_content?: UserProperty;
    kp_usr_utm_medium?: UserProperty;
    kp_usr_utm_campaign?: UserProperty;
  };
};

/**
 * ClientOnboarding - Single-step onboarding for clients (passengers, executive assistants)
 *
 * Steps:
 * 1. Registration form (company, job title, contact number, how did you hear about us)
 *
 * After completion:
 * - Calls POST /v3/auth/register/client API with:
 *   - provider_code: from utm_campaign (e.g., "hooli")
 *   - type: "passenger", "executive_assistant", or "travel_manager" (from utm_medium)
 *   - User info from Kinde (email, first_name, last_name) + form data
 * - This creates the client account and links them to the operator
 * - Redirects to "/" (home)
 *
 * Note: Clients are already associated with operators via utm_campaign during registration,
 * so they skip the Platform Setup step entirely.
 */
export const ClientOnboarding = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const siteInfo = useAppSelector(siteInfoSelector);
  const { getClaims, user } = useKindeAuth();
  const [getAccounts] = useLazyGetAccountsQuery();
  const [getProfile] = useLazyGetProfileQuery();
  const [verifyProvider] = useLazyVerifyProviderQuery();
  const [providerCode, setProviderCode] = useState<string>("");
  const [userType, setUserType] = useState<AccountType>(AccountType.PASSENGER);
  const [isCheckingOnboarding, setIsCheckingOnboarding] = useState(true);

  // Check if user has completed onboarding by checking profile metadata
  useEffect(() => {
    // First fetch and set the active account, then check profile
    getAccounts()
      .unwrap()
      .then((res) => {
        const accounts = res.data.accounts;
        const primaryAccount = accounts.find((account) => account.primary);

        if (!primaryAccount) {
          console.error("No primary account found");
          setIsCheckingOnboarding(false);
          return;
        }

        // Set active account in Redux so profile API can use it
        dispatch(setActiveAccount(primaryAccount));

        // Now fetch profile to check registration status
        return getProfile().unwrap();
      })
      .then((profile) => {
        if (profile?.accountMetadata?.isRegisterComplete) {
          navigate("/auth-callback", { replace: true });
        } else {
          setIsCheckingOnboarding(false);
        }
      })
      .catch(() => {
        setIsCheckingOnboarding(false);
      });
  }, [getAccounts, getProfile, dispatch, navigate]);

  useEffect(() => {
    getClaims("idToken")
      .then((claims) => {
        const typedClaims = claims as ClaimsWithUserProperties;
        const utmCampaign = typedClaims?.user_properties?.kp_usr_utm_campaign?.v;
        const utmMedium = typedClaims?.user_properties?.kp_usr_utm_medium?.v;

        if (utmCampaign) {
          setProviderCode(utmCampaign);

          // Fetch and set the active operator from UTM campaign
          if (siteInfo?.apiUrl) {
            verifyProvider({
              providerCode: utmCampaign,
              apiUrl: siteInfo.apiUrl,
            })
              .unwrap()
              .then((operator) => {
                dispatch(setActiveOperator(operator));
              })
              .catch((error) => {
                console.error("Error setting active operator:", error);
              });
          }
        }

        const clientTypes = [AccountType.EXECUTIVE_ASSISTANT, AccountType.TRAVEL_MANAGER] as string[];
        if (utmMedium && clientTypes.includes(utmMedium)) {
          setUserType(utmMedium as AccountType);
        } else {
          setUserType(AccountType.PASSENGER);
        }
      })
      .catch((error) => {
        console.error("Error fetching Kinde claims:", error);
      });
  }, [getClaims, verifyProvider, dispatch, siteInfo]);

  const handleRegistrationComplete = () => {
    navigate("/auth-callback", { replace: true });
  };

  // Show loading while checking if user has completed onboarding
  if (isCheckingOnboarding) {
    return <Loading />;
  }

  return (
    <div className="flex min-h-screen flex-row overflow-hidden">
      <div className="relative flex flex-1 flex-col">
        <GeneralHeader action="logout" />
        <Container className="relative flex w-full max-w-[1040px] flex-1 items-start justify-center gap-5 px-5 py-8">
          <div className="flex w-full justify-center">
            <ClientRegistrationStep
              onRegistrationComplete={handleRegistrationComplete}
              providerCode={providerCode}
              userType={userType}
              userEmail={user?.email || ""}
              userFirstName={user?.givenName || ""}
              userLastName={user?.familyName || ""}
            />
          </div>
        </Container>
        <GeneralFooter />
      </div>
    </div>
  );
};
